import { format, parseISO } from 'date-fns'

import { ACTIVITY_DAYS } from './constants'
import type { MetricPoint } from './useAdminMetrics'
import type { AdminUserVmDto } from './useAllVms'

export interface VmActivityPoint {
  date: string
  created: number
  // null when the VM list could not be loaded
  terminated: number | null
}

const DAY_MS = 24 * 60 * 60 * 1000

// Metrics series are UTC days, so the client-side counts bucket by UTC too.
function utcDayKey(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function lastUtcDays(now: Date): string[] {
  return Array.from({ length: ACTIVITY_DAYS }, (_, index) =>
    utcDayKey(new Date(now.getTime() - (ACTIVITY_DAYS - 1 - index) * DAY_MS)),
  )
}

function terminatedByDay(vms: AdminUserVmDto[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const vm of vms) {
    if (!vm.terminatedAt) continue
    const day = utcDayKey(parseISO(vm.terminatedAt))
    counts.set(day, (counts.get(day) ?? 0) + 1)
  }
  return counts
}

export function buildVmActivity(
  newVms: MetricPoint[],
  vms: AdminUserVmDto[] | undefined,
  now = new Date(),
): VmActivityPoint[] {
  const created = new Map(newVms.map((point) => [point.day, point.value]))
  const terminated = vms ? terminatedByDay(vms) : null

  return lastUtcDays(now).map((day) => ({
    date: format(parseISO(day), 'MMM d'),
    created: created.get(day) ?? 0,
    terminated: terminated ? (terminated.get(day) ?? 0) : null,
  }))
}

// vodopad names statuses as the DB enum does (graph @cloudless/fluence, #2183).
export function countLaunchedVms(
  vmsByStatus: { status: string; count: number }[],
): number {
  return vmsByStatus
    .filter((entry) => entry.status.toLowerCase() === 'launched')
    .reduce((total, entry) => total + entry.count, 0)
}
